import { Box, SvgIcon, Theme, useMediaQuery } from '@mui/material';
import { Button, ConditionalTooltip } from '@atoms';
import { CustomStyles } from '@types';
import {
  employeeDetailsClick,
  EVENT_EMPLOYEE_DETAILS_SELECT_PRODUCT,
} from '@modules/employeeDetails/events';
import { Product } from '@modules/employeeDetails/tabs/productsTab/useProducts';
import styles from './styles';

interface ItemInterface {
  title: string;
  icon: React.ReactNode;
  account_number: string;
  onClick: () => void;
}
type ItemKeysType = keyof Omit<ItemInterface, 'icon' | 'onClick'>;
interface TabSelectorProps {
  items: [ItemInterface];
  selectedTab: Product;
  setSelectedTab: () => void;
  keyProp: string;
  titleProp: string;
  customStyle: {
    text?: CustomStyles;
    button?: CustomStyles;
    icon?: CustomStyles;
  };
  rightButton: {
    text: string;
    onClick: () => void;
    icon: React.ReactNode;
  };
}

/**
 * TabSelector
 * @component
 * @param props
 * @param props.items options of the tab selector
 * @param props.selectedTab
 * @param props.keyProp the prop to use for key
 * @param props.titleProp the prop to show in the title
 * @param props.customStyle
 * @param props.rightButton
 * @param props.rightButton.text
 * @param props.rightButton.onClick
 * @param props.rightButton.icon
 */
const TabSelector = ({
  items,
  selectedTab,
  keyProp,
  titleProp,
  customStyle = {},
  rightButton,
}: TabSelectorProps) => {
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const combinedButtonStyles = {
    button: {
      ...styles.button.button,
      ...customStyle.button,
    },
    text: {
      ...styles.button.text,
      ...customStyle.text,
    },
    icon: {
      ...styles.button.icon,
      ...customStyle.icon,
    },
  };

  return (
    <Box sx={styles.container(responsive)}>
      <Box sx={styles.containerResponsive(responsive)}>
        {items?.map((item, index) => {
          const parsedKey = titleProp as ItemKeysType;
          const title = item[parsedKey];
          const text = Array.isArray(title) ? title.join(', ') : title;

          const isSelected = selectedTab.account_number === item.account_number;
          return (
            <ConditionalTooltip tooltipText={text} key={index}>
              <Button
                key={item[keyProp as ItemKeysType]}
                variant={isSelected ? 'outlined' : 'text'}
                startIcon={<SvgIcon sx={styles.button.icon(isSelected)}>{item.icon}</SvgIcon>}
                content={text}
                onClick={() => {
                  employeeDetailsClick(EVENT_EMPLOYEE_DETAILS_SELECT_PRODUCT);
                  item.onClick();
                }}
                customStyle={combinedButtonStyles}
              />
            </ConditionalTooltip>
          );
        })}
      </Box>
      {rightButton && (
        <Box sx={styles.rightButton}>
          <Button
            startIcon={<SvgIcon sx={styles.button.icon(false)}>{rightButton.icon}</SvgIcon>}
            text={rightButton.text}
            onClick={() => rightButton.onClick()}
            customStyle={combinedButtonStyles}
          />
        </Box>
      )}
    </Box>
  );
};

export default TabSelector;
